/**
 * Modal URL Management System
 * 
 * Centralized, type-safe modal URL management for the Hub application.
 * 
 * ## Features
 * - Type-safe modal ID and parameter mapping
 * - Automatic URL construction and parsing
 * - Integration with global modal stack
 * - Validation and error handling
 * - Development-mode debugging
 * 
 * ## Usage
 * 
 * ### Opening a modal with entity ID:
 * ```tsx
 * import { useModalControls } from '@/lib/modals';
 * 
 * function DealCard({ deal }) {
 *   const { openModal } = useModalControls('deals-detail');
 *   
 *   return (
 *     <button onClick={() => openModal({ dealsId: deal.id })}>
 *       View Deal
 *     </button>
 *   );
 * }
 * ```
 * 
 * ### Building a link to a modal:
 * ```tsx
 * import { useModalLinks } from '@/lib/modals';
 * 
 * function DealLink({ deal }) {
 *   const { buildUrl } = useModalLinks();
 *   
 *   return (
 *     <Link href={buildUrl('deals-detail', { dealsId: deal.id })}>
 *       View Deal
 *     </Link>
 *   );
 * }
 * ```
 * 
 * ### Checking modal state from URL:
 * ```tsx
 * import { useModalUrlState } from '@/lib/modals';
 * 
 * function PageHeader() {
 *   const modalState = useModalUrlState('deals-detail');
 *   
 *   if (modalState.isOpen) {
 *     return <h1>Viewing Deal {modalState.entityId}</h1>;
 *   }
 *   
 *   return <h1>Deals</h1>;
 * }
 * ```
 * 
 * ## Migration from old system
 * 
 * ### Before:
 * ```tsx
 * // Manual URL construction
 * const url = `/deals?modal=deals-detail&dealsId=${id}`;
 * router.push(url);
 * ```
 * 
 * ### After:
 * ```tsx
 * import { useModalControls } from '@/lib/modals';
 * 
 * const { openModal } = useModalControls('deals-detail');
 * openModal({ dealsId: id });
 * ```
 */

// Configuration
export {
  MODAL_URL_CONFIG,
  getModalConfig,
  isModalConfigured,
  getConfiguredModalIds,
  getModalsByEntity,
  isModalUrlEnabled,
  parseHubModalUrlState,
  extractEntityId,
  extractModalParams,
  removeEntityIdParams,
  buildCleanUrl,
  type ModalConfig,
  type ConfiguredModalId,
  type ModalParams,
  type ParsedHubModalUrlState,
} from "./url/modal-url-config";

// URL Building
export {
  buildModalUrl,
  parseModalUrl,
  hasModalInUrl,
  getEntityIdFromUrl,
  buildModalParams,
  validateModalParams,
  updateModalParams,
  type BuildModalUrlOptions,
  type ValidationResult,
} from "./url/modal-url-builder";

// React Hooks
export {
  useModalControls,
  useOpenModal,
  useModalUrlState,
  useModalLinks,
  type ModalControls,
  type ModalControlOptions,
} from "./hooks/use-modal-controls";

export {
  useHubModalController,
  type HubModalController,
  type HubModalOpenOptions,
  type HubModalCloseOptions,
} from "./hooks/use-hub-modal-controller";

// Modal <-> browser history
export { HubModalHistoryProvider } from "./history/HubModalHistoryProvider";
export { useModalHistorySync } from "./history/use-modal-history-sync";
export {
  reconcileModalHistory,
  type ModalHistoryReconciliation,
} from "./history/modal-history-reconcile";
export {
  createModalHistoryStore,
  useModalHistoryStore,
  ModalHistoryContext,
  type ModalHistoryEntry,
  type ModalHistoryStore,
} from "./history/modal-history-store";

// Error Handling
export {
  ModalUrlError,
  ModalUrlErrorType,
  assertModalConfigured,
  assertValidModalParams,
  getModalConfigSafe,
  useModalUrlValidation,
  debugModalUrl,
  useDebugModalUrl,
  handleModalError,
  isModalUrlError,
  checkModalUrlSufficiency,
  useModalUrlCheck,
} from "./url/modal-url-errors";
